import { Box, Stack, useTheme } from "@mui/material";
import { memo } from "react";
import SidebarTabButton from "./SidebarTabButton";
import { SidebarTabId } from "./types";
import { SvgIconProps } from "@mui/material/SvgIcon";

interface RightSidebarNavProps {
  tabs: Array<{
    id: SidebarTabId;
    label: string;
    icon: React.ComponentType<SvgIconProps>;
  }>;
  selectedTabId: SidebarTabId | null;
  onTabChange: (tabId: SidebarTabId) => void;
  onOpenSidebar: () => void;
  onCloseSidebar: () => void;
  isSidebarOpen: boolean;
  getBadgeContent: (tabId: SidebarTabId) => number;
}

/**
 * RightSidebarNav - Generic vertical navigation bar for sidebar
 *
 * Features:
 * - Renders tab buttons dynamically from configuration
 * - Shows badge counts
 * - Highlights active tab when sidebar is open
 * - Allows toggling sidebar visibility
 */
const RightSidebarNav = memo(
  ({
    tabs,
    selectedTabId,
    onTabChange,
    onOpenSidebar,
    onCloseSidebar,
    isSidebarOpen,
    getBadgeContent,
  }: RightSidebarNavProps) => {
    const theme = useTheme();

    return (
      <Box
        data-testid="aa-sidebar-nav"
        sx={{
          height: "100%",
          border: 1,
          borderColor: "divider",
          borderTop: "none",
          borderBottom: "none",
          backgroundColor: theme.palette.background.paper,
          transition: "width 0.3s",
          width: 65,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Stack
          direction="column"
          alignItems="center"
          gap={0.5}
          sx={{
            marginX: isSidebarOpen ? 1.5 : 0,
            marginY: 1,
          }}
        >
          {tabs.map((tab) => (
            <SidebarTabButton
              key={tab.id}
              label={tab.label}
              icon={tab.icon}
              badgeContent={getBadgeContent(tab.id)}
              isSelected={selectedTabId === tab.id && isSidebarOpen}
              isSidebarOpen={isSidebarOpen}
              onSelect={() => onTabChange(tab.id)}
              onOpenSidebar={onOpenSidebar}
              onCloseSidebar={onCloseSidebar}
            />
          ))}
        </Stack>
      </Box>
    );
  },
);

RightSidebarNav.displayName = "RightSidebarNav";

export default RightSidebarNav;
